import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	computed,
	ElementRef,
	inject,
	Injector,
	linkedSignal,
	OnInit,
	signal,
	viewChild
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonButtonRowWidget, CommonDateTimeField, CommonDateTimeFieldConfig, CommonDateTimeFieldWidget, CommonForm, CommonOptionsFieldConfig, CommonSelectField, CommonSelectFieldWidget, CommonTextField, CommonTextFieldWidget, UtilityService } from '@eforall/common';
import { DashboardDialogFramePart, DialogContext, DialogService } from '@eforall/dashboard';
import { DialogMode, EngagementContactInteractionForm, StaffData } from '@interfaces';
import { AppService, DomainDataService } from '../../../../services';
import { StaffSelectorWidget } from '../../staff-selector';
import { EngagementInteractionFormService } from './engagement-interaction-form.service';

export interface EngagementInteractionFormDialogData {
	mode: DialogMode;
	engagementId: number;
	engagementContactId?: number;
}

interface Payload { engagementContactId: number | undefined, engagementId: number, contactedDateUTC: number, staffId: number | undefined, contactMethodId: number, note: string, wasDefaultContactMethod: boolean }

@Component({
	selector: 'engagement-interaction-form-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		CommonSelectFieldWidget,
		CommonDateTimeFieldWidget,
		StaffSelectorWidget,
		CommonTextFieldWidget,
		CommonButtonRowWidget
	],
	templateUrl: './engagement-interaction-form.dialog.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	"styles": [
		`
			.white-box {
				background-color: #eeeeee;
				padding: 1em;
				margin-bottom: 0.5em;
			}
		`
	]
})

export class EngagementInteractionFormDialog implements OnInit, AfterViewInit {

	public dialogRef = inject(MatDialogRef<EngagementInteractionFormDialog>);
	public dialogContext: DialogContext<EngagementInteractionFormDialogData> = inject(MAT_DIALOG_DATA);

	private readonly dialogService = inject(DialogService);
	private readonly domainData = inject(DomainDataService);
	private readonly util = inject(UtilityService);
	private readonly app = inject(AppService);
	private readonly service = inject(EngagementInteractionFormService);
	private readonly injector = inject(Injector);

	public formSubmitButton = viewChild.required<ElementRef<HTMLButtonElement>>('interactionSaveBtn');
	private readonly form = new CommonForm<EngagementContactInteractionForm>(this.injector);
	public readonly dialogMode = DialogMode;
	public readonly showStaffSelector = signal<boolean>(false);
	public readonly isEnabledForm = computed(() => {
		return (this.dialogContext.data?.mode !== this.dialogMode.VIEW)
	})
	public readonly headerTitle = computed(() => {
		const data = this.dialogContext.data;
		if (data?.mode == DialogMode.ADD) return 'Add Interaction';
		if (data?.mode == DialogMode.EDIT) return 'Edit Interaction';
		return 'View Interaction';
	})

	public readonly staffUserId = linkedSignal(() => this.app.data().user.userId);
	public staffData = signal<StaffData[]>([]);
	public values = signal<EngagementContactInteractionForm>({
		wasDefaultContactMethod: false,
		engagementContactId: undefined,
		contactMethodId: 0,
		staffId: undefined,
		note: '',
		contactedDateUTC: this.util.date.toUTC(new Date())!,
	});

	private readonly contactMethodOptions = computed(() => {
		const domainData = this.domainData.data();
		return domainData.contactMethods.map(contactMethod =>
			({ value: contactMethod.contactMethodId, text: contactMethod.name })
		).sort((a, b) => a.text < b.text ? -1 : 1);
	});

	readonly contactMethod = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Contact Method', options: this.contactMethodOptions(), isRequired: true, isSpanish: false,
			isEnabled: this.isEnabledForm,
		}),
		actualValue: computed(() => this.values().contactMethodId || 0),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		individualSave: async (value: number) => {
			const values = this.values();
			this.values.set({ ...values, contactMethodId: value });
		},
		error: signal(''),
		formFieldName: 'contact-method',
		form: this.form,
	}));


	readonly note = computed<CommonTextField>(() => ({
		config: signal({ label: 'Note', min: 0, max: 50, fixCase: 'sentence-case', isRequired: false, multiLine: false, isSpanish: false, isEnabled: signal(true) }),
		actualValue: computed(() => this.values().note || ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		individualSave: async (value: string) => {
			const values = this.values();
			this.values.set({ ...values, note: value || '' });
		},
		error: signal(''),
		formFieldName: 'note',
		form: this.form,
	}));

	readonly contactedDate = computed<CommonDateTimeField>(() => ({
		config: signal<CommonDateTimeFieldConfig>({
			label: 'Contacted Date', isRequired: true, isSpanish: false, type: 'event', includeTime: false,
			isEnabled: this.isEnabledForm,
		}),
		actualValue: computed(() => this.values().contactedDateUTC),
		pendingValue: signal<number | undefined | null>(null),
		saving: signal(false),
		individualSave: async (value: number | undefined) => {
			const values = this.values();
			this.values.set({ ...values, contactedDateUTC: value || 0 });
		},
		error: signal(''),
		formFieldName: 'contacted-date',
		form: this.form,
	}));

	async ngOnInit(): Promise<void> {
		this.staffData.set(this.app.data().staff);
		const data = this.dialogContext.data;
		if (data?.mode === DialogMode.EDIT || data?.mode === DialogMode.VIEW) {
			const engagementContactId = data?.engagementContactId;
			if (engagementContactId) {
				const interaction = await this.service.getInteractionById(engagementContactId, data?.engagementId);
				if (interaction) {
					this.values.set({
						...interaction,
					});
					this.staffUserId.set(interaction.staffId || this.app.data().user.userId);
				}
			}
		}
		this.showStaffSelector.set(true);
	}

	ngAfterViewInit() {
		this.form.setSubmitButton(
			this.formSubmitButton(),
			async () => {
				await this.onSaveInteraction();
			}
		);
	}

	checkIfValid() {
		const values = this.values();
		return !!values.staffId && !!values.contactMethodId && !!values.contactedDateUTC
			&& !this.contactMethod().error() && !this.contactedDate().error();
	}

	handleStaffValueChange = (value: { staffId?: number; fullName?: string } | undefined) => {
		const values = this.values();
		if (value?.staffId) this.values.set({ ...values, staffId: value?.staffId, });
	};

	async onSaveInteraction() {
		const data = this.dialogContext.data;
		if (!data) return;

		if (this.checkIfValid()) {

			const payload: Payload = {
				engagementContactId: data.engagementContactId,
				engagementId: data.engagementId,
				contactedDateUTC: this.values().contactedDateUTC,
				staffId: this.values().staffId,
				contactMethodId: this.values().contactMethodId,
				note: this.values().note,
				wasDefaultContactMethod: this.domainData.data().contactMethodMap[this.values().contactMethodId]?.isDefault ?? false
			};

			let engagementContactId: number | undefined;
			if (data.mode === DialogMode.ADD) {
				engagementContactId = await this.service.addEngagementContactInteraction(payload);
			} else {
				engagementContactId = await this.service.updateEngagementContactInteraction(payload);
			}
			this.closeDialog();
		} else {
			this.dialogService.showMessage('Invalid Form', 'Invalid form please fill out all required fields', 'Ok', [], 400, 200);
		}
	}

	async closeDialog() {
		this.dialogRef.close();
	}
}